import React, { useState } from 'react';
import { Button, Menu, MenuItem, Divider, Snackbar } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import axios from 'axios';
import { exportToPdf, getVisibleColumns } from './exportPdf';
import { exportToXlsx, rowsToCsv, downloadText, safeName } from './exportXlsx';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const BUTTON_STYLE = {
  color: 'black',
  borderColor: 'black',
  padding: '2px 8px',
  minWidth: '60px',
  fontSize: '0.75rem',
};

// Best-effort audit of a client-side export; never blocks or fails the download.
function logExport(view, format, rows) {
  if (!rows) return;
  axios.post(`${API_BASE_URL}/exports/log`, { view, format, rows }, { withCredentials: true }).catch(() => {});
}

async function downloadServerExport(view, format, params) {
  const res = await axios.get(`${API_BASE_URL}/exports/${view}`, {
    params: { ...params, fmt: format }, responseType: 'blob', withCredentials: true,
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  a.download = `${view}-${stamp}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Unified export dropdown (CSV / XLSX / PDF) used across all tables.
 *
 * Two ways to feed it:
 *   MRT table   – table + getAllRows() (+ getSelectedRows()) + onExportCsv(rows)
 *   plain list  – data (array of flat objects) + columns ([{header, key}]); CSV/XLSX/PDF are built for you
 *
 * Props:
 *   title               – PDF/file title; also the audit-log view name for client exports
 *   onExportCsv(rows)   – MRT mode: export these rows as CSV
 *   onExportXlsx(rows)  – (optional) overrides the default XLSX of the MRT rows
 *   preparePdfData(rows) – (optional) custom data transform for PDF; defaults to rows.map(r => r.original)
 *   onExportAllCsv()    – (optional) overrides "Export All to CSV" entirely
 *   serverExport        – (optional) { view, params }: adds "all rows" CSV/XLSX items served by GET /exports/{view}
 */
export default function ExportMenu({
  onExportCsv, onExportXlsx, table, title = 'Export', getAllRows, getSelectedRows, preparePdfData,
  onExportAllCsv, data, columns, serverExport,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const close = () => setAnchorEl(null);

  const plain = Array.isArray(data);
  const hasSelection = !plain && typeof getSelectedRows === 'function';
  const selectedEmpty = hasSelection && (!getSelectedRows() || getSelectedRows().length === 0);

  const mrtData = (rows) => (preparePdfData ? preparePdfData(rows) : rows.map((r) => r.original));
  const mrtColumns = () => (!preparePdfData && table ? getVisibleColumns(table) : undefined);

  const csv = (rows) => {
    if (plain) {
      downloadText(rowsToCsv(data, columns || Object.keys(data[0] || {})), `${safeName(title)}.csv`);
      logExport(title, 'csv', data.length);
    } else if (onExportAllCsv && rows === 'all') {
      onExportAllCsv();
    } else {
      const r = rows === 'all' ? getAllRows() : rows;
      onExportCsv(r);
      logExport(title, 'csv', r.length);
    }
  };
  const guarded = (fn) => async (rows) => {
    try { await fn(rows); } catch { setFailed(true); }
  };
  const xlsx = guarded(async (rows) => {
    if (plain) {
      await exportToXlsx(data, { title, columns });
      logExport(title, 'xlsx', data.length);
      return;
    }
    const r = rows === 'all' ? getAllRows() : rows;
    if (onExportXlsx) onExportXlsx(r); else await exportToXlsx(mrtData(r), { title, columns: mrtColumns() });
    logExport(title, 'xlsx', r.length);
  });
  const pdf = guarded(async (rows) => {
    if (plain) {
      await exportToPdf(data, { title, columns });
      logExport(title, 'pdf', data.length);
      return;
    }
    const r = rows === 'all' ? getAllRows() : rows;
    await exportToPdf(mrtData(r), { title, columns: mrtColumns() });
    logExport(title, 'pdf', r.length);
  });
  const server = async (format) => {
    setBusy(true);
    try {
      await downloadServerExport(serverExport.view, format, serverExport.params);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const all = hasSelection ? 'All ' : '';
  const run = (fn, arg) => () => { fn(arg); close(); };

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant="outlined"
        size="small"
        startIcon={<FileDownloadIcon />}
        endIcon={<ArrowDropDownIcon />}
        style={BUTTON_STYLE}
        aria-label="Export"
      >
        Export
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        {hasSelection && (
          <MenuItem disabled={selectedEmpty} onClick={() => { csv(getSelectedRows()); close(); }}>Export Selected to CSV</MenuItem>
        )}
        <MenuItem onClick={run(csv, 'all')}>{hasSelection ? 'Export All to CSV' : 'Export to CSV'}</MenuItem>
        <Divider />
        {hasSelection && (
          <MenuItem disabled={selectedEmpty} onClick={() => { xlsx(getSelectedRows()); close(); }}>Export Selected to XLSX</MenuItem>
        )}
        <MenuItem onClick={run(xlsx, 'all')}>{hasSelection ? 'Export All to XLSX' : 'Export to XLSX'}</MenuItem>
        <Divider />
        {hasSelection && (
          <MenuItem disabled={selectedEmpty} onClick={() => { pdf(getSelectedRows()); close(); }}>Export Selected to PDF</MenuItem>
        )}
        <MenuItem onClick={run(pdf, 'all')}>{`Export ${all}to PDF`}</MenuItem>
        {serverExport && <Divider />}
        {serverExport && (
          <MenuItem disabled={busy} onClick={() => { server('csv'); close(); }}>Download every row (CSV)</MenuItem>
        )}
        {serverExport && (
          <MenuItem disabled={busy} onClick={() => { server('xlsx'); close(); }}>Download every row (XLSX)</MenuItem>
        )}
      </Menu>
      <Snackbar open={busy} message="Preparing export…" />
      <Snackbar open={failed} autoHideDuration={4000} onClose={() => setFailed(false)} message="Export failed — try again or narrow the filters" />
    </>
  );
}
